import { DialogRef } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TenantKey } from '@app/core/enums/tenant-key.enum';
import { waitForNavigationEnd } from '@domains/auth/components/login-modal/operators/wait-for-login-navigation.operator';
import { ConfirmationModalData } from '@shared/components/confirmation-modal/confirmation-modal.model';
import { ToastService } from '@shared/components/toast';
import { DashboardNavigationService, LoaderService } from '@shared/services';
import {
  extractErrorMessage,
  getObjectToFormData,
  getPhoneNumberToPlainNumber,
  getStateCourt,
} from '@shared/utils';
import { tap, finalize, catchError, of } from 'rxjs';
import { CLERK_CREATE_APP_TOAST_CONFIG } from '../../shared/constants/clerk-create-app-toast.constants';
import { getConfirmationData } from '../../shared/helpers/get-confirmation-data.helper';
import { ClerkCreateApplicationHttpService } from '../../shared/services/clerk-create-application-http.service';
import { FormErrorService } from '@shared/components';
import { RegisterMinorFormService } from './register-minor-form.service';
import { ClerkCreateMinorAppPayload } from './interfaces/clerk-create-minor-app.interface';
import { MinorOverviewStateService } from '@app/minor-estates/shared/services';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ClerkCreateMinorFacadeService {
  private readonly _formService = inject(RegisterMinorFormService);
  private readonly _formErrorService = inject(FormErrorService);
  private readonly _minorOverviewService = inject(MinorOverviewStateService);

  private readonly _toast = inject(ToastService);
  private readonly _router = inject(Router);
  private readonly _clerkCreateApplicationHttpService = inject(
    ClerkCreateApplicationHttpService,
  );

  private readonly _currentCourt = getStateCourt();

  private readonly _dashboardNavigationService = inject(
    DashboardNavigationService,
  );
  private readonly _loaderService = inject(LoaderService);

  private readonly _dialogRef = inject(DialogRef);

  private readonly _confirmationData: ConfirmationModalData =
    getConfirmationData("Minor's Estates");

  private readonly _form = this._formService.form;

  public getConfirmationData(): ConfirmationModalData {
    return this._confirmationData;
  }

  public submitApplication() {
    if (this._form.invalid) return;

    const rawPayload = this._form.getRawValue();
    const payload: ClerkCreateMinorAppPayload = {
      email: rawPayload.email,
      is_firm: rawPayload.isFirm,
      first_name: rawPayload.firstName,
      middle_name: rawPayload.middleName,
      last_name: rawPayload.lastName,
      username: rawPayload.username,
      user_id: rawPayload.userId === 0 ? null : String(rawPayload.userId),
      phone_number: getPhoneNumberToPlainNumber(rawPayload.phoneNumber),
      prefix_phone: rawPayload.prefixPhone,
      court_order: rawPayload.courtOrder,
      affidavit: rawPayload.affidavit,
      relationship_to_minor: rawPayload.relationshipToMinor!,
      minor_first_name: rawPayload.minorFirstName,
      minor_last_name: rawPayload.minorLastName,
      minor_date_of_birth: formatDate(
        rawPayload.minorDateOfBirth!,
        'yyyy-MM-dd',
        'en-US',
      ),
    };

    const formattedPayload = getObjectToFormData(payload);

    this._loaderService.show();
    this._clerkCreateApplicationHttpService
      .createClerkMinor(Number(this._currentCourt().id), formattedPayload)
      .pipe(
        tap(({ minorApplicationId }) => {
          this._minorOverviewService.setMinorApplicationId(
            Number(minorApplicationId),
          );
          this._dashboardNavigationService.goToDashboard(TenantKey.MINOR);
        }),
        waitForNavigationEnd(this._router, this._toast),
        tap(() => {
          this._dialogRef.close();
        }),
        finalize(() => {
          this._loaderService.hide();
        }),
        catchError((error) => {
          const errorMessage = extractErrorMessage(error);

          this._formErrorService.displaySimpleErrorModal(
            errorMessage,
            'Error in Register',
          );

          this._toast.show(CLERK_CREATE_APP_TOAST_CONFIG['error']);
          return of(null);
        }),
      )
      .subscribe();
  }
}
